import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useAuth } from "../../utils/auth";
import { useApi } from "../../utils/api";

export default function SetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [linkExpired, setLinkExpired] = useState(false);
  const [email, setEmail] = useState("");
  const [newLinkSent, setNewLinkSent] = useState(false);
  const { user, login } = useAuth();
  const { getCurrentUser, getUserProducts } = useApi();

  const auth = getAuth();
  const query = new URLSearchParams(useLocation().search);
  const oobCode = query.get("oobCode");

  useEffect(() => {
    async function fetchUserData() {
      try {
        if (!oobCode) {
          navigate("/");
          setLoading(false);
          return;
        }

        const email = await verifyPasswordResetCode(auth, oobCode);
        if (email) {
          setEmail(email);
          setLoading(false);
        } else {
          setError("User not found");
          setLoading(false);
        }
      } catch (err) {
        if (
          err.code === "auth/invalid-action-code" ||
          err.code === "auth/expired-action-code"
        ) {
          setError("Link expired. Enter your email to get a new link.");
          setLinkExpired(true);
          setLoading(false);
        } else {
          setError(
            "Failed to fetch user data. Enter your email to get a new link."
          );
          setLinkExpired(true);
          setLoading(false);
        }
      }
    }

    fetchUserData();
  }, []);

  useEffect(() => {
    if (!user) return;
    const checkRedirect = async () => {
      try {
        const [profile, assignedProducts, purchasedProducts] = await Promise.all([
          getCurrentUser(),
          getUserProducts(user.uid, "assigned"),
          getUserProducts(user.uid, "purchased"),
        ]);
        const hasAssignedProducts =
          Array.isArray(assignedProducts) && assignedProducts.length > 0;
        const hasPurchasedProducts =
          Array.isArray(purchasedProducts) && purchasedProducts.length > 0;
        if (
          hasAssignedProducts &&
          !profile?.stripeCustomerId &&
          !hasPurchasedProducts
        ) {
          navigate("/payment", { replace: true });
        } else {
          navigate("/workouts", { replace: true });
        }
      } catch {
        navigate("/workouts", { replace: true });
      }
    };
    checkRedirect();
  }, [user, getCurrentUser, getUserProducts, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (linkExpired) {
      await sendPasswordResetEmail(auth, email);
      setNewLinkSent(true);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, password);
      login(email, password);
    } catch (err) {
      console.error("Error creating user:", err);
      if (err.code === "auth/email-already-in-use") {
        setError("An account with this email already exists");
      } else {
        setError("Failed to create account. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (newLinkSent) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            New Link Sent
          </Typography>
          <Alert severity="success">
            A new link has been sent to your email.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%" }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Set Your Password
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={linkExpired ? false : true}
              variant="filled"
            />
            {!linkExpired && (
              <>
                <TextField
                  margin="normal"
                  fullWidth
                  id="password"
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />

                <TextField
                  margin="normal"
                  fullWidth
                  id="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              {linkExpired ? "Get New Link" : "Set Password"}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}
